import classNames from 'classnames';
import { cloneElement, type HTMLAttributeAnchorTarget, memo, type ReactElement, useMemo } from 'react';
import { Link, useLocation } from 'react-router';

export const NavigationLink = memo(
  ({
    name,
    href,
    icon,
    rightIcon,
    className = '',
    navigable = true,
    target,
    subMenu = false,
    visible = true,
    control = false,
    onLinkClick,
  }: {
    name: string;
    href?: string;
    icon?: ReactElement;
    rightIcon?: ReactElement;
    className?: string;
    navigable?: boolean;
    target?: HTMLAttributeAnchorTarget;
    subMenu?: boolean;
    visible?: boolean;
    control?: boolean;
    onLinkClick?: () => void;
  }) => {
    const location = useLocation();

    const containsPath = useMemo(() => href && location.pathname.startsWith(href), [href, location.pathname]);

    if (!visible) {
      return null;
    }

    const NavigationLinkContent = (
      <div
        className={classNames(
          `flex items-center rounded px-2 py-1.5 ${className}`,
          {
            'bg-blue-6/10': containsPath && !control,
            'hover:bg-gray-0': navigable,
          },
          { 'pl-8': subMenu },
        )}
      >
        {icon && (
          <span className="h-6 w-6">
            {cloneElement(icon, {
              className: classNames({
                'fill-gray-7 text-gray-7': !containsPath,
                'fill-blue-7 text-blue-7': containsPath,
              }),
              height: 24,
              width: 24,
            })}
          </span>
        )}
        <span
          className={classNames('pl-2 font-semibold', {
            'text-blue-6': containsPath && !control,
            'text-gray-7': !containsPath && subMenu,
          })}
        >
          {name}
        </span>

        {rightIcon && (
          <span className="ml-auto mr-1.5 flex h-6 w-6 items-center">
            {cloneElement(rightIcon, { height: 16, width: 16 })}
          </span>
        )}
      </div>
    );

    if (!navigable || !href) {
      return NavigationLinkContent;
    }

    return (
      <Link className="!text-gray-9" target={target} to={href} onClick={onLinkClick}>
        {NavigationLinkContent}
      </Link>
    );
  },
);
